import { ReactElement, useCallback, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';

import Event, { EVENT_REG_TYPES } from '../../types/Event';
import GratiCard, { GratiCardSlotClickHandlers } from '../common/GratiCard';

import {
  getEventCategory,
  getEventTitle,
  getEventStatusText,
  getEventStats,
  getEventDetails,
  EventSpotlight,
} from './eventCardUtils';
import { PreviewElement } from './eventPreviewTypes';

interface EventCardPreviewProps {
  event: Event;
  variant?: 'elevation' | 'outlined' | 'none';
  /** When true, shows signup status as if currently active (for preview editing) */
  simulateSignupActive?: boolean;
  /** Called when a card element is clicked (for interactive preview) */
  onElementClick?: (element: PreviewElement) => void;
}

/**
 * A preview version of EventCard that doesn't depend on Firestore hooks.
 * Used for previewing events before they are saved.
 *
 * Key differences from EventCard:
 * - No registration hooks (useEventReg, etc.)
 * - No ActionBar (no signup/withdraw buttons)
 * - No TopActions menu
 * - No partner selection UI
 * - Reactive to event prop changes for real-time preview updates
 * - Supports interactive mode via onElementClick for edit preview
 */
export default function EventCardPreview({
  event,
  variant = 'outlined',
  simulateSignupActive = false,
  onElementClick,
}: EventCardPreviewProps): ReactElement {
  const [ggidSnackbarOpen, setGgidSnackbarOpen] = useState(false);
  const [emailSnackbarOpen, setEmailSnackbarOpen] = useState(false);

  const handleGgidCopy = useCallback(() => {
    navigator.clipboard.writeText(`https://www.golfgenius.com/ggid/${event.ggid}`);
    setGgidSnackbarOpen(true);
  }, [event.ggid]);

  const handleEmailCopy = useCallback(() => {
    navigator.clipboard.writeText(event.registrationEmail!);
    setEmailSnackbarOpen(true);
  }, [event.registrationEmail]);

  // When simulating active signup, adjust dates so status shows as "signup open"
  const previewEvent = useMemo(() => {
    return {
      ...event,
    };
  }, [event]);

  const teamSize = event.teamStructure?.size;

  // Build a split subtitle when there's a per-team limit (each part separately clickable)
  const subtitle = useMemo(() => {
    const statsText = getEventStats(previewEvent);
    if (!onElementClick || !teamSize) return statsText;

    const perTeamText = ` · ${teamSize} per team`;
    const spotsText = statsText.replace(perTeamText, '');
    return (
      <>
        <span onClick={(e) => { e.stopPropagation(); onElementClick('subtitle'); }} style={{ cursor: 'pointer' }}>{spotsText}</span>
        <span onClick={(e) => { e.stopPropagation(); onElementClick('perTeam'); }} style={{ cursor: 'pointer' }}>{perTeamText}</span>
      </>
    );
  }, [onElementClick, teamSize, previewEvent]);

  // Build slot click handlers for interactive preview mode
  const slotClickHandlers: GratiCardSlotClickHandlers | undefined = useMemo(() => {
    if (!onElementClick) return undefined;

    const details = getEventDetails(event);
    const hasContactEmail = !!(event.registrationEmail && event.registrationType !== EVENT_REG_TYPES.EMAIL);
    const emailOffset = hasContactEmail ? 1 : 0;
    const costIndex = event.cost ? details.length - 1 - emailOffset : -1;
    const ggidIndex = event.ggid ? (event.cost ? details.length - 2 - emailOffset : details.length - 1 - emailOffset) : -1;

    return {
      colorbar: () => onElementClick('colorbar'),
      avatar: () => onElementClick('logo'),
      title: () => onElementClick('title'),
      // subtitle click is handled per-span when team size is set
      ...(teamSize ? {} : { subtitle: () => onElementClick('subtitle') }),
      category: () => onElementClick('category'),
      spotlight: () => onElementClick('date'),
      state: () => onElementClick('status'),
      description: () => onElementClick('description'),
      detail: (index: number) => {
        if (index === ggidIndex) return onElementClick('description');
        if (index === costIndex) return onElementClick('cost');
        return onElementClick(index === 0 ? 'time' : 'location');
      },
    };
  }, [onElementClick, event, teamSize]);

  // Render actionbar with appropriate action when in interactive mode
  const actionbar = useMemo(() => {
    if (!simulateSignupActive || !onElementClick) return undefined;

    switch (event.registrationType) {
      case EVENT_REG_TYPES.EXTERNAL:
        if (event.registrationExternalForm) {
          return (
            <Button variant="contained" fullWidth onClick={() => onElementClick('status')}>
              Get registration form
            </Button>
          );
        } else {
          return (
            <Typography variant="bodyMedium" onClick={() => onElementClick('status')} sx={{ cursor: 'pointer' }}>
              Contact organizer for details.
            </Typography>
          );
        }
      case EVENT_REG_TYPES.EMAIL:
        return (
          <Typography variant="bodyMedium" onClick={() => onElementClick('status')} sx={{ cursor: 'pointer' }}>
            {event.registrationEmail
              ? `Register with ${event.registrationEmail}.`
              : 'Register by email.'}
          </Typography>
        );
      default:
        return (
          <Button variant="contained" fullWidth onClick={() => onElementClick('status')}>
            Sign up
          </Button>
        );
    }
  }, [simulateSignupActive, onElementClick, event.registrationType, event.registrationExternalForm, event.registrationEmail]);

  return (
    <>
      <GratiCard
        key={event.itemId || 'preview'}
        variant={variant}
        colorbar={previewEvent.color}
        avatar={previewEvent.logo}
        avatarStorageLocation={
          previewEvent.orgId && previewEvent.leagueId && previewEvent.itemId
            ? `/org/${previewEvent.orgId}/league/${previewEvent.leagueId}/event/${previewEvent.itemId}/orgMemberLeagueAdminFile`
            : undefined
        }
        category={getEventCategory(previewEvent)}
        state={getEventStatusText(previewEvent)}
        spotlight={<EventSpotlight event={previewEvent} />}
        title={getEventTitle(previewEvent)}
        subtitle={subtitle}
        details={getEventDetails(previewEvent, event.ggid ? handleGgidCopy : undefined, undefined, event.registrationEmail ? handleEmailCopy : undefined)}
        description={previewEvent.description}
        actionbar={actionbar}
        slotClickHandlers={slotClickHandlers}
      />
      <Snackbar open={ggidSnackbarOpen} autoHideDuration={3000} onClose={() => setGgidSnackbarOpen(false)}>
        <Alert severity="success" onClose={() => setGgidSnackbarOpen(false)}>GGID link copied to clipboard</Alert>
      </Snackbar>
      <Snackbar open={emailSnackbarOpen} autoHideDuration={3000} onClose={() => setEmailSnackbarOpen(false)}>
        <Alert severity="success" onClose={() => setEmailSnackbarOpen(false)}>Email copied to clipboard</Alert>
      </Snackbar>
    </>
  );
}
